import { notification } from "antd";
import { makeAutoObservable } from "mobx";
import { onError } from "src/common/onError";
import { ZObjectItem } from "src/types/ZObjectItem";
import { ZUid } from "src/types/zUid";
import { nodeRemoveMessage, removeAction, saveIntent } from "../kinds/registry";
import { SaveContext, SaveResult } from "../kinds/types";
import { CatalogStore } from "../model/catalog";
import { ObjectDetailsStore } from "../model/details";
import { httpErrorKind } from "../model/httpError";
import { NodeId, TreeNode, objectNodeId, unsavedId } from "../model/node";
import { ObjectServicesStore } from "../model/objectServices";
import { SelectionStore } from "../model/selection";
import { TreeStore } from "../model/tree";
import { NodeLoading } from "./loading";
import { NodeSelection } from "./selection";

/**
 * Изменения дерева: сохранение, удаление, появление новых объектов.
 *
 * После записи структура объекта могла измениться где угодно, поэтому объект
 * перечитывается целиком, а не латается по частям, как в v1. Лишнего запроса
 * при этом нет там, где сервер уже вернул данные.
 */
export class ObjectsMutations {
  constructor(
    private readonly tree: TreeStore,
    private readonly catalog: CatalogStore,
    private readonly details: ObjectDetailsStore,
    private readonly services: ObjectServicesStore,
    private readonly selection: SelectionStore,
    private readonly loading: NodeLoading,
    private readonly nodeSelection: NodeSelection,
    private readonly ctx: SaveContext,
  ) {
    makeAutoObservable<
      ObjectsMutations,
      | "catalog"
      | "ctx"
      | "details"
      | "loading"
      | "nodeSelection"
      | "selection"
      | "services"
      | "tree"
    >(this, {
      tree: false,
      catalog: false,
      details: false,
      services: false,
      selection: false,
      loading: false,
      nodeSelection: false,
      ctx: false,
    });
  }

  deletingId: NodeId | null = null;

  deletingWait = false;

  get deletingMessage(): string {
    const node = this.deletingId ? this.tree.node(this.deletingId) : undefined;
    return node ? nodeRemoveMessage(node) : "Удалить?";
  }

  setDeleting(id: NodeId | null) {
    this.deletingId = id;
  }

  private setDeletingWait(on: boolean) {
    this.deletingWait = on;
  }

  /** Подтверждённое удаление. Окно закрывается последним действием. */
  async removeDeleting() {
    const node = this.deletingId ? this.tree.node(this.deletingId) : undefined;
    if (!node) return;
    try {
      this.setDeletingWait(true);
      await this.remove(node);
      this.setDeleting(null);
    } catch (e) {
      onError(e);
    } finally {
      this.setDeletingWait(false);
    }
  }

  canSave(node: TreeNode): boolean {
    return saveIntent(node, this.ctx) !== null;
  }

  async save(values: unknown) {
    const id = this.selection.selectedId;
    const node = id ? this.tree.node(id) : undefined;
    if (!node) return;
    const save = saveIntent(node, this.ctx);
    if (!save) throw Error("Этот узел сохранять нельзя");
    const wasDraftObject = node.kind === "object" && node.data.id === unsavedId;
    const result = await save(values);
    // Объект получил настоящий идентификатор: черновик больше не нужен
    if (wasDraftObject) this.catalog.remove(unsavedId);
    await this.applyResult(result);
  }

  async remove(node: TreeNode) {
    const run = removeAction(node, this.ctx);
    if (!run) throw Error("Этот узел удалять нельзя");
    await run();
    const objectId = this.tree.ownerObject(node.id)?.data.id;
    const insideRemoved = this.isSelectionInside(node.id);

    if (node.kind === "object") {
      this.loading.forgetObject(node.data.id);
    } else {
      this.tree.remove(node.id);
      if (objectId !== undefined) {
        // Кэш деталей ещё содержит удалённое: без сброса перечитывание
        // восстановит узел, который только что убрали
        this.details.forget(objectId);
        await this.refreshObject(objectId);
      }
    }
    if (insideRemoved) await this.nodeSelection.select(null);
  }

  /**
   * Объект, созданный вне дерева: копирование, импорт.
   *
   * Из ответа берётся только имя для каталога. Как данные объекта он не годится
   * по той же причине, что и ответ на создание: списка прав в нём нет, и объект
   * выглядел бы запрещённым к изменению.
   */
  async addObject(details: ZObjectItem) {
    this.catalog.upsert(this.catalog.fromDetails(details));
    await this.refreshObject(details.id);
    await this.nodeSelection.select(objectNodeId(details.id));
  }

  /**
   * Порядок групп и атрибутов изменён редактором порядка.
   *
   * Новый порядок не разыгрывается по узлам: у редактора своё дерево со своими
   * ключами, и сводить два представления по ключам значило бы жёстко их
   * связать. Вместо этого объект перечитывается целиком - заодно корректно
   * отрабатывает перенос узла к другому владельцу, который иначе пришлось бы
   * обрабатывать отдельно.
   */
  async reorderApplied(objectId: ZUid) {
    this.details.forget(objectId);
    await this.refreshObject(objectId);
  }

  private async applyResult(result: SaveResult) {
    this.details.forget(result.objectId);
    await this.refreshObject(result.objectId);
    if (result.selectId) await this.nodeSelection.select(result.selectId);
  }

  /** Перечитать объект и обновить каталог и уже раскрытые ветви. */
  private async refreshObject(objectId: ZUid) {
    if (objectId === unsavedId) return;
    try {
      const details = await this.details.ensure(objectId);
      await this.services.reload(objectId);
      const prev = this.catalog.find(objectId);
      this.catalog.upsert(this.catalog.fromDetails(details, prev));
      await this.loading.reloadLoaded(objectNodeId(objectId));
    } catch (error) {
      if (httpErrorKind(error) === "notFound") {
        // Объект удалили, пока мы его сохраняли: показывать его дальше нельзя
        this.loading.forgetObject(objectId);
        notification.warning({ message: "Объект был удалён" });
        return;
      }
      notification.warning({
        message: "Не удалось обновить объект после сохранения",
      });
    }
  }

  private isSelectionInside(nodeId: NodeId): boolean {
    const { selectedId } = this.selection;
    return (
      selectedId !== null && this.tree.subtreeIds(nodeId).includes(selectedId)
    );
  }
}
